import {
  MOVE_EMPLOYEE_LEAVING,
  MOVE_GROUP,
  MOVE_NEW_HIRE
} from '../components/kiosk/KioskContent/MoveSequence/MoveSequence'
import * as AbWorkflow from './util/workflow'
import {_L} from '../context/LocaleContext'

const getActivityLogParameters = ({move, user, isMoveProject = false}) => {
  const {startDate, type, moveEmployees} = move
  const [moveEmployee] = moveEmployees
  const {siteId, building, floor, room} = moveEmployee
  const dateScheduled = startDate || new Date()

  return {
    'activity_log.site_id': siteId,
    'activity_log.bl_id': building.value,
    'activity_log.fl_id': floor.value,
    'activity_log.rm_id': room.value,
    'activity_log.requestor': user.employee.id,
    'activity_log.date_scheduled': dateScheduled,
    'activity_log.description': isMoveProject
      ? move.description
      : move.instructions,
    'activity_log.activity_type':
      type === MOVE_GROUP
        ? 'SERVICE DESK - GROUP MOVE'
        : 'SERVICE DESK - INDIVIDUAL MOVE'
  }
}

const createActivityLogRecord = ({move, user, isMoveProject}) => {
  const activityLogParameters = getActivityLogParameters({
    move,
    user,
    isMoveProject
  })

  return AbWorkflow.callMethod(
    'AbEssentialFacility-RequestsService-submitRequest',
    [null, activityLogParameters]
  ).then(({code, data}) => {
    // TODO: Check result

    const {activity_log_id} = data
    return Promise.resolve(activity_log_id)
  })
}

export const requestIndividualMove = async ({
  move,
  ruleName,
  projectId = null,
  user,
  isMoveProject = false,
  localeState
}) => {
  const activityLogParameters = getActivityLogParameters({
    move,
    user,
    isMoveProject
  })

  const {data, code, message} = await AbWorkflow.callMethod(
    'AbEssentialFacility-RequestsService-submitRequest',
    [null, activityLogParameters]
  )

  if (code === 'executed') {
    const activityLogId = data.activity_log_id
    await requestIndividualMoveForActivityLogRecord({
      move,
      activityLogId,
      projectId,
      ruleName,
      user,
      localeState
    })
  } else {
    throw Error(message)
  }
}

const requestIndividualMoveForActivityLogRecord = async ({
  move,
  activityLogId,
  projectId,
  ruleName,
  user,
  isProject = false,
  moveEmployeeIndex = 0,
  localeState
}) => {
  const parameters = getMoveParameters({
    move,
    activityLogId,
    projectId,
    user,
    isProject,
    moveEmployeeIndex,
    localeState
  })

  const {data, code, message} = await AbWorkflow.callMethod(ruleName, [
    parameters,
    move.moveFurniture,
    move.moveEquipment
  ])

  if (code !== 'executed') {
    throw Error(message)
  }

  const moveId = data.record['mo.mo_id']

  const {code: moveCode, message: moveMessage} = await AbWorkflow.callMethod(
    'AbEssentialFacility-MoveService-requestIndividualMove',
    [moveId]
  )

  if (moveCode !== 'executed') {
    throw Error(moveMessage)
  }
}

const getMoveParameters = ({
  move,
  activityLogId,
  projectId,
  isProject = false,
  user,
  moveEmployeeIndex = 0,
  localeState
}) => {
  const {type, moveEmployees} = move
  let moveType = 'Employee'
  if (type === MOVE_NEW_HIRE) {
    moveType = 'New Hire'
  } else if (type === MOVE_EMPLOYEE_LEAVING) {
    moveType = 'Leaving'
  }

  const YES = _L({key: 'Yes', localeState})
  const NO = _L({key: 'No', localeState})
  const MOVE_EQUIPMENT = _L({key: 'Move Equipment', localeState})

  const description = isProject
    ? move.description
    : `${move.instructions} [${MOVE_EQUIPMENT} - ${
        move.moveEquipment ? YES : NO
      }]`

  const employee = moveEmployees[moveEmployeeIndex]
  const {building, floor, room, startDate, employee: moveEmployee} = employee

  const parameters = {
    'mo.mo_type': moveType,
    'mo.requestor': user.employee.id,
    'mo.dept_contact': user.employee.id,
    'mo.em_id': moveEmployee.value,
    'mo.date_start_req': move.startDate || new Date(),
    'mo.date_scheduled': move.startDate || new Date(),
    'mo.description': description,
    'mo.activity_log_id': activityLogId
  }

  if (type !== MOVE_EMPLOYEE_LEAVING) {
    parameters['mo.to_bl_id'] = building.value
    parameters['mo.to_fl_id'] = floor.value
    parameters['mo.to_rm_id'] = room.value
    parameters['mo.date_to_perform'] = startDate || new Date()
  }

  if (type === MOVE_GROUP) {
    parameters['mo.project_id'] = projectId
  }

  return parameters
}

export const requestGroupMove = ({move, user, localeState}) => {
  const {
    department,
    moveEmployees,
    moveEquipment,
    numEmployees,
    instructions,
    projectId,
    requestor,
    deptContact,
    contact
  } = move
  const {employee} = user

  const [dvId, dpId] = department.value.split('|')

  const groupMoveLabel = _L({key: 'Group Move', localeState})
  const moveEquipmentLabel = _L({key: 'Move Equipment', localeState})
  const YES = _L({key: 'Yes', localeState})
  const NO = _L({key: 'No', localeState})
  const moveEq = moveEquipment ? YES : NO

  const description = `${groupMoveLabel}: ${dvId} - ${dpId} (${numEmployees})\n${instructions} [${moveEquipmentLabel} - ${moveEq}]`

  let startDate = moveEmployees[0].startDate
  let endDate = startDate

  // Get date range from the moveEmployees

  moveEmployees.forEach(move => {
    const {startDate: moveStartDate} = move
    if (moveStartDate < startDate) {
      startDate = moveStartDate
    } else if (moveStartDate > endDate) {
      endDate = moveStartDate
    }
  })

  const parameters = {
    'project.project_id': projectId,
    'project.requestor': requestor,
    'project.dept_contact': deptContact,
    'project.contact_id': contact,
    'project.bl_id': employee.blId,
    'project.dv_id': dvId,
    'project.dp_id': dpId,
    'project.description': description,
    'project.date_start': startDate,
    'project.date_end': endDate
  }

  let createdProjectId = ''

  return AbWorkflow.callMethod(
    'AbEssentialFacility-CapitalBudgetingService-createProject',
    [parameters]
  )
    .then(({code, dataSet, message}) => {
      if (code === 'executed') {
        createdProjectId = dataSet.getValue('project.project_id')
        parameters['project.project_id'] = createdProjectId

        return AbWorkflow.callMethod(
          'AbEssentialFacility-MoveService-addProjectMoveRecord',
          [parameters]
        )
      } else {
        return Promise.reject(message)
      }
    })
    .then(({code, message}) => {
      // create activity log record for all moves
      return createActivityLogRecord({
        move,
        user,
        isMoveProject: true
      })
    })
    .then(activityLogId => {
      let fn = moveEmployees.map((moveEmployee, idx) => {
        // const {moveFurniture, moveEquipment} = move
        // let moveItem = {...moveEmployee, ...{moveFurniture}, ...{moveEquipment}}
        return requestIndividualMoveForActivityLogRecord.bind(this, {
          move: move,
          activityLogId,
          projectId: createdProjectId,
          user,
          ruleName: 'AbEssentialFacility-MoveService-addIndividualMove',
          isProject: true,
          moveEmployeeIndex: idx,
          localeState
        })
      })

      return fn.reduce((p, f) => {
        return p.then(f)
      }, Promise.resolve())
    })
    .then(result => {
      return AbWorkflow.callMethod(
        'AbEssentialFacility-MoveService-requestGroupMove',
        [createdProjectId]
      )
    })
    .catch(message => {
      return Promise.reject(message)
    })
}
